import { ChangeDetectionStrategy, Component, ElementRef, inject, signal, ViewChild } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { TranslatePipe } from '@ngx-translate/core';
import { UserService } from '../../../common/service/user.service';
import { NotificationService } from '../../../common/service/notification.service';
import { environment } from '../../../environment/environment';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { CustomerService } from '../../../common/service/customer.service';

import { ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { TranslateService } from '@ngx-translate/core';

interface CustomerPreference {
  id: number;
  customerNumber: string;
  logoPath: string;
}

@Component({
  selector: 'gate-customer-logo-settings',
  standalone: true,
  imports: [ButtonModule, TranslatePipe, ConfirmDialog],
  templateUrl: './customer-logo-settings.component.html',
  styleUrl: './customer-logo-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService],
})
export class CustomerLogoSettingsComponent {
  private readonly userService = inject(UserService);
  private readonly notificationService = inject(NotificationService);
  private readonly httpClient = inject(HttpClient);
  private readonly customerService = inject(CustomerService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly translateService = inject(TranslateService);

  isLoading = signal(false);
  customerPreference = signal<CustomerPreference | null>(null);
  currentLogoUrl = signal<string | null>(null);
  selectedFile = signal<File | null>(null);
  previewUrl = signal<string | null>(null);

  @ViewChild('logoFileInput') private logoFileInput?: ElementRef<HTMLInputElement>;

  // The logo is always edited for the customer the user is currently working in.
  private readonly customerNumber = this.userService.getCustomerNumber();

  private readonly customerPreferenceEndpoint = environment.APIBase + 'CustomerPreference';
  private readonly allowedExtensions = ['png', 'jpg', 'jpeg', 'webp', 'gif'];
  private readonly maxFileSize = 5 * 1024 * 1024;

  constructor() {
    this.loadCustomerPreference();
  }

  cancelSelectedFile(): void {
    this.selectedFile.set(null);
    this.previewUrl.set(null);
    if (this.logoFileInput) {
      this.logoFileInput.nativeElement.value = '';
    }
  }

  private loadCustomerPreference(): void {
    const customerNumber = this.customerNumber;
    if (!customerNumber) {
      this.customerPreference.set(null);
      this.currentLogoUrl.set(null);
      return;
    }

    this.isLoading.set(true);
    this.customerService.getCustomersBatch([customerNumber])
      .then((customers) => {
        const customer = customers.find((item) => item.id === customerNumber) ?? customers[0];
        this.customerPreference.set(customer?.logoPath ? {
          id: 0,
          customerNumber: customer.id,
          logoPath: customer.logoPath,
        } : null);
        this.currentLogoUrl.set(this.customerService.resolveCustomerLogoUrl(customer?.logoPath));
      })
      .catch(() => {
        this.customerPreference.set(null);
        this.currentLogoUrl.set(null);
      })
      .finally(() => {
        this.isLoading.set(false);
      });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      const extension = file.name.split('.').pop()?.toLowerCase();
      if (!extension || !this.allowedExtensions.includes(extension)) {
        this.notificationService.showNotification(
          'error',
          'customerPreference.invalidFileType.title',
          'customerPreference.invalidFileType.message'
        );
        input.value = '';
        return;
      }

      if (file.size > this.maxFileSize) {
        this.notificationService.showNotification(
          'error',
          'customerPreference.fileTooLarge.title',
          'customerPreference.fileTooLarge.message'
        );
        input.value = '';
        return;
      }

      this.selectedFile.set(file);

      const reader = new FileReader();
      reader.onload = (e) => {
        this.previewUrl.set(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  }

  uploadLogo(): void {
    const file = this.selectedFile();
    if (!file) return;

    const customerNumber = this.customerNumber;
    if (!customerNumber) return;

    this.isLoading.set(true);
    const formData = new FormData();
    formData.append('customerNumber', customerNumber);
    formData.append('file', file);

    firstValueFrom(
      this.httpClient.post<CustomerPreference>(`${this.customerPreferenceEndpoint}/logo`, formData)
    )
      .then((preference) => {
        this.notificationService.showNotification(
          'success',
          'customerPreference.upload.success.title',
          'customerPreference.upload.success.message'
        );
        this.customerPreference.set(preference);
        const logoPath = this.customerService.updateCachedCustomerLogo(preference.customerNumber, preference.logoPath);
        this.currentLogoUrl.set(this.customerService.resolveCustomerLogoUrl(logoPath) ?? this.previewUrl());
        this.cancelSelectedFile();
      })
      .catch(() => {
        this.notificationService.showNotification(
          'error',
          'customerPreference.upload.error.title',
          'customerPreference.upload.error.message'
        );
      })
      .finally(() => {
        this.isLoading.set(false);
      });
  }

  deleteLogo(): void {
    const customerNumber = this.customerNumber;
    if (!customerNumber) return;

    this.confirmationService.confirm({
      header: this.translateService.instant('customerPreference.remove.confirmTitle'),
      message: this.translateService.instant('customerPreference.remove.confirmMessage'),
      icon: 'pi pi-exclamation-triangle',
      acceptButtonProps: {
        label: this.translateService.instant('customerPreference.removeLogo'),
        severity: 'danger',
      },
      rejectButtonProps: {
        label: this.translateService.instant('cancelButton'),
        severity: 'secondary',
      },
      accept: () => {
        this.performDeleteLogo(customerNumber);
      },
    });
  }

  private performDeleteLogo(customerNumber: string): void {
    this.isLoading.set(true);
    firstValueFrom(
      this.httpClient.delete<CustomerPreference>(`${this.customerPreferenceEndpoint}/logo/${customerNumber}`)
    )
      .then(() => {
        this.notificationService.showNotification(
          'success',
          'customerPreference.remove.success.title',
          'customerPreference.remove.success.message'
        );
        this.customerPreference.set(null);
        this.customerService.updateCachedCustomerLogo(customerNumber, '');
        this.currentLogoUrl.set(null);
        this.selectedFile.set(null);
        this.previewUrl.set(null);
      })
      .catch(() => {
        this.notificationService.showNotification(
          'error',
          'customerPreference.remove.error.title',
          'customerPreference.remove.error.message'
        );
      })
      .finally(() => {
        this.isLoading.set(false);
      });
  }

  triggerFileInput(): void {
    this.logoFileInput?.nativeElement.click();
  }
}
